import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEntitlement, OPEN_SIGN_IN, rememberPaywall } from '../context/EntitlementContext';
import { track } from '../services/analytics';
import { PRO_FEATURES } from '../config/entitlements';
import { platform } from '../services/platform';

const PRO_BENEFITS = Object.values(PRO_FEATURES).map((f) => f.title);

// Annual first: it is the plan most likely to cover the app's fixed yearly costs.
const PACKAGE_ORDER = ['ANNUAL', 'LIFETIME', 'MONTHLY'];
const sortPackages = (packages) => [...packages].sort((a, b) => {
  const rank = (p) => (PACKAGE_ORDER.indexOf(p.packageType) + 1) || PACKAGE_ORDER.length + 1;
  return rank(a) - rank(b);
});

function trialLabel(product) {
  const intro = product?.introPrice;
  if (!intro || intro.price !== 0) return '';
  const unit = String(intro.periodUnit || '').toLowerCase();
  return `${intro.periodNumberOfUnits}-${unit} free trial, then `;
}

const PERIOD_LABELS = { ANNUAL: 'per year', MONTHLY: 'per month', SIX_MONTH: 'per 6 months', THREE_MONTH: 'per 3 months', WEEKLY: 'per week', LIFETIME: 'one-time' };

// Renders its children for Pro users and a locked card for everyone else.
export function ProGate({ feature, children }) {
  const { isPro, ready, openPaywall, webPlans } = useEntitlement();
  if (isPro) return children;
  const trial = webPlans.find((plan) => plan.trial);

  const info = PRO_FEATURES[feature] || {};
  return (
    <div className="terminal-card p-6 my-6 text-center border-terminal-amber-dim">
      <p className="text-terminal-amber text-xs font-mono uppercase tracking-wider mb-2">Ultronic Pro</p>
      <h3 className="font-display font-bold text-terminal-green uppercase tracking-wider text-base mb-2">{info.title}</h3>
      <p className="text-txt-secondary text-sm mb-4 max-w-md mx-auto">{info.description}</p>
      <button
        onClick={() => openPaywall(feature)}
        disabled={!ready}
        className="glow-btn glow-btn-amber px-6 font-mono text-sm disabled:opacity-40"
      >
        {ready ? 'Unlock Pro' : 'Loading...'}
      </button>
      {trial && <p className="text-txt-secondary text-xs font-mono mt-2">{trial.trial.replace(/, then\s*$/, '')} on the {trial.title.toLowerCase()} plan.</p>}
      <p className="mt-2">
        <Link to="/plans" className="inline-block py-3 text-terminal-cyan text-xs font-mono uppercase tracking-wider hover:underline">What is free and what is Pro</Link>
      </p>
    </div>
  );
}

export function PaywallModal() {
  const { paywallFeature, closePaywall, packages, purchase, restore, refresh, isPro, channel, webPlans, signedIn } = useEntitlement();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const close = useCallback(() => {
    setMessage('');
    closePaywall();
  }, [closePaywall]);
  useEffect(() => {
    if (!paywallFeature) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || busy) return;
      track('paywall_dismissed');
      close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [paywallFeature, busy, close]);

  if (!paywallFeature) return null;

  const run = async (action) => {
    setBusy(true);
    setMessage('');
    const result = await action();
    setBusy(false);
    if (result.ok) close();
    else if (!result.cancelled) setMessage(result.message || '');
  };

  const web = channel === 'web';
  const signIn = () => {
    rememberPaywall(paywallFeature);
    close();
    window.dispatchEvent(new CustomEvent(OPEN_SIGN_IN, { detail: { mode: 'register' } }));
  };
  const dismiss = () => {
    if (busy) return;
    track('paywall_dismissed');
    close();
  };

  const storeName = platform === 'ios' ? 'Apple ID' : 'Google Play';

  return (
    <div className="fixed inset-0 z-[110] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Ultronic Pro">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative min-h-full flex items-center justify-center py-6">
        <div className="relative w-full max-w-md mx-4 terminal-card p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-terminal-amber text-xs font-mono uppercase tracking-wider">Ultronic Pro</p>
              <h2 className="font-display font-bold text-terminal-green crt-glow uppercase tracking-wider text-lg">
                {PRO_FEATURES[paywallFeature]?.title || 'Unlock everything'}
              </h2>
            </div>
            <button onClick={dismiss} aria-label="Close" className="text-txt-secondary hover:text-terminal-green p-3 -m-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <ul className="space-y-1 text-sm text-txt-primary">
            {PRO_BENEFITS.map((benefit) => (
              <li key={benefit} className="flex gap-2"><span className="text-terminal-green font-mono">+</span>{benefit}</li>
            ))}
          </ul>

          {isPro ? (
            <p className="text-terminal-green text-sm font-mono">Pro is active on this account.</p>
          ) : web ? (
            <div className="space-y-2">
              {!signedIn && (
                <div className="rounded border border-terminal-amber-dim p-3">
                  <p className="text-txt-primary text-sm mb-2">
                    Pro is tied to a free account, so it works on this website wherever you sign in. Create one or sign in, and this offer will still be here.
                  </p>
                  <button onClick={signIn} className="w-full glow-btn glow-btn-green font-mono text-sm">Create account or sign in</button>
                </div>
              )}
              {signedIn && webPlans.map((plan) => (
                <button
                  key={plan.id}
                  disabled={busy}
                  onClick={() => run(() => purchase(plan))}
                  className={`w-full glow-btn ${plan.best ? 'glow-btn-green' : 'glow-btn-amber'} flex flex-col items-start gap-1 font-mono text-sm disabled:opacity-40 normal-case text-left`}
                >
                  <span className="flex w-full items-center justify-between gap-3">
                    <span>{plan.title}</span>
                    {plan.best && <span className="text-xs uppercase tracking-wider">Best value</span>}
                  </span>
                  <span className="text-xs">{plan.trial || ''}{plan.price} {plan.period}</span>
                </button>
              ))}
              {!signedIn && (
                <ul className="font-mono text-xs text-txt-secondary space-y-1 pt-1">
                  {webPlans.map((plan) => (
                    <li key={plan.id} className="flex justify-between gap-3"><span>{plan.title}</span><span>{plan.trial || ''}{plan.price} {plan.period}</span></li>
                  ))}
                </ul>
              )}
              {busy && <p className="text-terminal-green text-xs font-mono" role="status">Opening secure checkout...</p>}
            </div>
          ) : packages.length === 0 ? (
            <div>
              <p className="text-terminal-amber text-sm font-mono mb-3">
                Subscription options could not be loaded. Check your connection and try again.
              </p>
              <button
                disabled={busy}
                onClick={async () => { setBusy(true); await refresh(); setBusy(false); }}
                className="w-full glow-btn glow-btn-amber font-mono text-sm disabled:opacity-40"
              >
                {busy ? 'Loading...' : 'Try Again'}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {sortPackages(packages).map((pkg) => (
                <button
                  key={pkg.identifier}
                  disabled={busy}
                  onClick={() => run(() => purchase(pkg))}
                  className={`w-full glow-btn ${pkg.packageType === 'ANNUAL' ? 'glow-btn-green' : 'glow-btn-amber'} flex flex-col items-start gap-1 font-mono text-sm disabled:opacity-40 normal-case text-left`}
                >
                  <span className="flex w-full items-center justify-between gap-3">
                    <span>{pkg.product.title || pkg.identifier}</span>
                    {pkg.packageType === 'ANNUAL' && <span className="text-xs uppercase tracking-wider">Best value</span>}
                  </span>
                  <span className="text-xs">
                    {trialLabel(pkg.product)}{pkg.product.priceString} {PERIOD_LABELS[pkg.packageType] || ''}
                  </span>
                </button>
              ))}
            </div>
          )}

          {message && <p className="text-terminal-amber text-sm font-mono" role="alert">{message}</p>}

          {(!web || signedIn) && (
            <button
              disabled={busy}
              onClick={() => run(restore)}
              className="w-full py-3 text-xs font-mono uppercase tracking-wider text-terminal-cyan hover:underline disabled:opacity-40"
            >
              {web ? 'I already have Pro' : 'Restore Purchases'}
            </button>
          )}

          {web ? (
            <p className="text-xs text-txt-secondary">
              Stripe takes the payment on its own secure page, and we never see or store your card. A
              subscription renews automatically at the price shown until you cancel, which you can do at any time
              under Manage subscription on the Free and Pro page.{webPlans.some((plan) => plan.trial) ? ' A free trial is charged when it ends unless you cancel before then.' : ''} The
              one-time option does not renew.{' '}
              <Link to="/plans" onClick={close} className="text-terminal-cyan hover:underline">What is free and what is Pro</Link>.{' '}
              <Link to="/terms" onClick={close} className="text-terminal-cyan hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" onClick={close} className="text-terminal-cyan hover:underline">Privacy Policy</Link>.
            </p>
          ) : (
          <p className="text-xs text-txt-secondary">
            Payment is charged to your {storeName} account at confirmation, or at the end of any free trial.
            Subscriptions renew automatically at the price shown unless cancelled at least 24 hours before the end
            of the current period. The one-time option does not renew. Manage or cancel in
            your store account settings.{' '}
            <Link to="/plans" onClick={close} className="text-terminal-cyan hover:underline">What is free and what is Pro</Link>.{' '}
            <Link to="/terms" onClick={close} className="text-terminal-cyan hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" onClick={close} className="text-terminal-cyan hover:underline">Privacy Policy</Link>.
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
